const SPEECH_DEBOUNCE_MS = 400
const SELECT_SPEECH_DELAY_MS = 100
const MAX_SPOKEN_TEXT_LENGTH = 300
const RECT_HIT_TOLERANCE_PX = 4

interface CaretPosition {
  offsetNode: Node
}

type DocumentWithCaret = Document & {
  caretPositionFromPoint?: (x: number, y: number) => CaretPosition | null
}

const isBviSpeechEnabled = () =>
  decodeURIComponent(document.cookie)
    .split(";")
    .some(c => c.trim() === "bvi_speech=true")

const getTextNodeAtPoint = (x: number, y: number): Node | null => {
  if (typeof document.caretRangeFromPoint === "function") {
    const range = document.caretRangeFromPoint(x, y)
    if (range?.startContainer.nodeType === Node.TEXT_NODE)
      return range.startContainer

    return null
  }

  const doc = document as DocumentWithCaret
  if (typeof doc.caretPositionFromPoint === "function") {
    const pos = doc.caretPositionFromPoint(x, y)
    if (pos?.offsetNode.nodeType === Node.TEXT_NODE)
      return pos.offsetNode
  }

  return null
}

const isCursorOverTextNode = (node: Node, x: number, y: number): boolean => {
  const range = document.createRange()
  range.selectNode(node)

  return Array.from(range.getClientRects()).some(r =>
    x >= r.left - RECT_HIT_TOLERANCE_PX &&
    x <= r.right + RECT_HIT_TOLERANCE_PX &&
    y >= r.top - RECT_HIT_TOLERANCE_PX &&
    y <= r.bottom + RECT_HIT_TOLERANCE_PX,
  )
}

const getAriaLabel = (element: Element | null): string => {
  let el = element
  while (el) {
    const label = el.getAttribute("aria-label")?.trim()
    if (label) return label.slice(0, MAX_SPOKEN_TEXT_LENGTH)
    el = el.parentElement
  }
  return ""
}

const getTextAtPoint = (x: number, y: number): string => {
  const textNode = getTextNodeAtPoint(x, y)

  if (textNode && isCursorOverTextNode(textNode, x, y))
    return (textNode.textContent ?? "").trim().slice(0, MAX_SPOKEN_TEXT_LENGTH)

  const el = document.elementFromPoint(x, y)

  if (el instanceof HTMLImageElement && el.alt.trim())
    return el.alt.trim().slice(0, MAX_SPOKEN_TEXT_LENGTH)

  if (el instanceof HTMLSelectElement) {
    const selected = el.options[el.selectedIndex]
    const text = selected?.text?.trim()
    if (text) return text.slice(0, MAX_SPOKEN_TEXT_LENGTH)
  }

  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    const text = el.value.trim() || el.placeholder.trim()
    if (text) return text.slice(0, MAX_SPOKEN_TEXT_LENGTH)
  }

  const ariaLabel = getAriaLabel(el)
  if (ariaLabel) return ariaLabel

  return ""
}

const getSelectedOptionText = (select: HTMLSelectElement): string =>
  (select.options[select.selectedIndex]?.text ?? "")
    .trim()
    .slice(0, MAX_SPOKEN_TEXT_LENGTH)

const speakText = (text: string, locale: string) => {
  const hasCyrillic = /[а-яёА-ЯЁ]/.test(text)
  const hasLatin = /[a-zA-Z]/.test(text)
  const lang = hasCyrillic || !hasLatin
    ? (locale === "ru" ? "ru-RU" : "en-US")
    : "en-US"

  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = lang
  utterance.voice = speechSynthesis.getVoices().find(v => v.lang === lang) ?? null

  speechSynthesis.resume()
  speechSynthesis.speak(utterance)
}

const plugin: NuxtPlugin = ({ app }) => {
  if (!("speechSynthesis" in window))
    return

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let scheduledText = ""

  document.addEventListener("mousemove", (event: MouseEvent) => {
    if (!isBviSpeechEnabled())
      return

    const target = event.target as Element | null
    if (target?.closest(".bvi-panel"))
      return

    const text = getTextAtPoint(event.clientX, event.clientY)

    if (text && text === scheduledText)
      return

    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }

    speechSynthesis.cancel()
    scheduledText = text

    if (!text)
      return

    debounceTimer = setTimeout(() => {
      if (scheduledText !== text)
        return

      speakText(text, app.i18n.locale)
    }, SPEECH_DEBOUNCE_MS)
  })

  const speakSelect = (select: HTMLSelectElement, announcement: string) => {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }

    if (speechSynthesis.speaking || speechSynthesis.pending)
      speechSynthesis.cancel()

    scheduledText = getSelectedOptionText(select)

    setTimeout(() => {
      speakText(announcement, app.i18n.locale)
    }, SELECT_SPEECH_DELAY_MS)
  }

  document.addEventListener("change", (event: Event) => {
    if (!isBviSpeechEnabled())
      return

    const target = event.target
    if (!(target instanceof HTMLSelectElement))
      return

    const selectedText = getSelectedOptionText(target)
    if (!selectedText)
      return

    speakSelect(target, selectedText)
  })

  document.addEventListener("focusin", (event: Event) => {
    if (!isBviSpeechEnabled())
      return

    const target = event.target
    if (!(target instanceof HTMLSelectElement))
      return

    const label = target.getAttribute("aria-label")?.trim() ?? ""
    const announcement = [label, getSelectedOptionText(target)]
      .filter(Boolean)
      .join(": ")

    if (!announcement)
      return

    speakSelect(target, announcement)
  })
}

export default plugin
